import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { useFetchMmtPropertiesQuery } from '~/api/vehicles/VehicleManagement.api';
import { registerDataInterest, unregisterDataInterest } from '~/features/base/actions/ui/dataInterestActions';
import {
    devicesLoadingSelector,
    getCurrentlySelectedDeviceInfoSelector,
} from '~/features/devices/selectors/controlDeviceSelectors';
import DevicePropertiesTable from '~/features/devices/components/DevicePropertiesTable';
import { tbmVcmDeviceRowDescriptors } from '~/features/devices/constants/tbmVcmDeviceRowDescriptors';
import DeviceRolloutStatesContainer from '~/features/devices/components/DeviceRolloutStatesContainer';
import uid from '~/features/base/utils/uid';
import { fetchControlDeviceInformation } from '~/features/devices/actions/controlDeviceInfoActions';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { defaultNetworkCodeSelector } from '~/features/base/selectors/defaultNetworkCodeSelector';
import { CM4 } from '~/features/devices/constants/deviceTypes';
import { cm4DeviceRowDescriptors } from '~/features/devices/constants/cm4DeviceRowDescriptors';

interface Props {
    serialNumber: string;
    vin: string;
}

const DevicePropertiesContainer: React.FC<Props> = ({ serialNumber, vin }) => {
    const dispatch = useDispatch();
    const controlDeviceInfo = useSelector((state: any) => getCurrentlySelectedDeviceInfoSelector(state, { serialNumber, vin }));
    const defaultNetworkCodeList = useSelector(defaultNetworkCodeSelector);
    const name = useMemo(() => uid(), []);

    useEffect(() => {
        dispatch(registerDataInterest(name, [
            fetchControlDeviceInformation({ serialNumber }),
        ]));

        return () => {
            dispatch(unregisterDataInterest(name));
        };
    }, [name, serialNumber, dispatch]);

    const { data: mmtProperties, isLoading, error } = useFetchMmtPropertiesQuery(vin);
    const controlDeviceData = { ...controlDeviceInfo, mmtProperties: {...mmtProperties}, networkCodeList: defaultNetworkCodeList };
    const descriptors = controlDeviceInfo.type !== CM4 ? tbmVcmDeviceRowDescriptors : cm4DeviceRowDescriptors;

    return (
        <>
            <DevicePropertiesTable
                controlDeviceData={controlDeviceData}
                rowDescriptors={descriptors}
            />
            <div className='padding-top-5'>
                <ExpanderPanel bsStyle='primary' className='margin-0' open={true}
                               title={<FormattedMessage id='intl-msg:rolloutProgress'/>}>
                    <DeviceRolloutStatesContainer deviceType={controlDeviceInfo.type} serialNumber={controlDeviceInfo.serialNumber}/>
                </ExpanderPanel>
            </div>
        </>
    );
};

export default DevicePropertiesContainer;
